require('./RAStrategiesGraph.scss');

// @vendors
const React = require('react');
const { Fragment } = React;
const PropTypes = require('prop-types');
const classnames = require('classnames');
const lodash = require('lodash');

// @components
const RAContribution = require('./RAContribution/RAContribution');
const RAStrategy = require('./RAStrategy/RAStrategy');
const RAGoalBar = require('./RAGoalBar/RAGoalBar');
const FormattedTextWithComponents = require('components/broker/brokerFormattedTextWithComponents/brokerFormattedTextWithComponents');
const BlockInfo = require('components/contactCenter/blockInfo/blockInfo');

// @commons
const { OKLink } = require('commonsComponents/OKLink');
const { OKMessage } = require('commonsComponents/OKMessage');

// @utilities
const { formatText } = require('core/i18n').i18n;
const CurrencyHelper = require('utilities/currencyHelper');
const {
    getDay,
    getFullYear,
    getMonthNameFromDate,
} = require('utilities/dateHelper');
const { FormattedText } = require('core/i18n').i18n;
const { toFirstUppercase } = require('utilities/stringHelper');
const { recordLink } = require('utilities/tagging');

// @constants
const { EURO_SYMBOL } = require('constants/index');
const { MINIMUM_CONTRIBUTION_AMOUNT } = require('../../raConstants/index');
const PLUS_SIGN = '+';
const MINUS_SIGN = '-';
const BAR_GRAPH_SCALED = 'BAR_GRAPH_SCALED';
const INVESTMENT_EXCEED_GOAL = 'INVESTMENT_EXCEED_GOAL';

const ContactCenterRender = require('components/conditionalRender/contactCenterRender');
const WebappRender = require('components/conditionalRender/webappRender');

const getFilteredStrategies = (
    initialContributionAmount,
    immExpectedProfitability,
    maxInvestmentProfile,
    goal,
) => {
    const immProductsChartData = immExpectedProfitability.get('barChartData');
    if (!immProductsChartData) {
        return [];
    }

    return immProductsChartData.reduce((strategies, immProductData, index) => {
        let medProfitability;
        const up = immProductData.getIn([1, 'up']);
        const low = immProductData.getIn([1, 'low']);
        const contributionAmount = immProductData.getIn([1, 'contributionNet']);
        const recurringContributionAmount =
            contributionAmount - initialContributionAmount;
        const minProfitability = Math.abs(contributionAmount - low);
        const maxProfitability = Math.abs(contributionAmount - up);
        const graphScaledToMax =
            contributionAmount + maxProfitability <= goal * 0.3;
        const isLeftBarExceedGoal = contributionAmount > goal;

        let strategy = {
            id: index + 1,
            maxProfitability,
            recurringContributionAmount,
            graphScaledToMax,
            isLeftBarExceedGoal,
            up,
        };

        if (low - contributionAmount > 0) {
            medProfitability = low - contributionAmount;
            strategy.medProfitability = medProfitability; // set positive minimum profit
        } else {
            strategy.minProfitability = minProfitability; // set negative minimum profit
        }

        strategies.push(strategy);
        return strategies;
    }, []);
};

class RAStrategiesGraph extends React.Component {
    constructor() {
        super();

        this.state = {
            goalHeight: 0,
        };

        this.handlePortfolioDistributionClick = this.handlePortfolioDistributionClick.bind(
            this,
        );
        this.buildStrategy = this.buildStrategy.bind(this);
        this.buildStrategiesSection = this.buildStrategiesSection.bind(this);
    }

    componentDidMount() {
        this.updateGoalHeight();
    }

    componentDidUpdate(prevProps) {
        const prevStrategies = getFilteredStrategies(
            prevProps.initialContributionAmount,
            prevProps.immExpectedProfitability,
            prevProps.maxInvestmentProfile,
            prevProps.goal,
        );
        const strategies = getFilteredStrategies(
            this.props.initialContributionAmount,
            this.props.immExpectedProfitability,
            this.props.maxInvestmentProfile,
            this.props.goal,
        );

        if (
            prevStrategies.length !== strategies.length ||
            prevProps.showDescription !== this.props.showDescription ||
            prevProps.goal !== this.props.goal ||
            prevProps.initialContributionAmount !==
                this.props.initialContributionAmount
        ) {
            this.updateGoalHeight();
        }
    }

    handlePortfolioDistributionClick() {
        recordLink(
            'interaccion en inversion por objetivos',
            'ver distribucion de la cartera seleccionada',
        );
    }

    updateGoalHeight() {
        const {
            immExpectedProfitability,
            maxInvestmentProfile,
            initialContributionAmount,
            goal,
        } = this.props;
        const strategies = getFilteredStrategies(
            initialContributionAmount,
            immExpectedProfitability,
            maxInvestmentProfile,
            goal,
        );

        if (strategies.length === 0) {
            return;
        }

        // 105 is the approximate pixel height of each strategy card and 30 it's just a padding
        const goalHeight = strategies.length * 105 + 70;
        if (this.state.goalHeight !== goalHeight) {
            this.setState({ goalHeight });
        }
    }

    getGraphScaledToMaxValues(strategies) {
        const {
            goal,
            initialContributionAmount,
            selectedStrategy,
            singleStrategy,
        } = this.props;
        const smallBarScale = goal * 0.025;
        const bigBarScale = goal * 0.05;
        const isInitialAmountWidthScaled =
            bigBarScale > initialContributionAmount;
        const isLeftBarExceedGoal = lodash.some(strategies, {
            isLeftBarExceedGoal: true,
        });
        const maxProfitObj = strategies.reduce((prev, curr) => {
            return prev.maxProfitability > curr.maxProfitability ? prev : curr;
        }, 1);
        const graphScaledToMaxValuePoniter =
            (goal * 0.8) /
            (maxProfitObj.maxProfitability +
                initialContributionAmount +
                maxProfitObj.recurringContributionAmount);

        let minProfitability;
        let isBarWidthScaled;
        let isRecurringWidthScaled = false;
        let isGroupScaledEnabled = lodash.some(strategies, {
            graphScaledToMax: true,
        });

        if (singleStrategy && strategies.length > 0) {
            const currentStrategy = strategies.find(function(s) {
                return s.id === selectedStrategy;
            });

            if (currentStrategy.recurringContributionAmount) {
                isRecurringWidthScaled =
                    bigBarScale > currentStrategy.recurringContributionAmount;
            }

            isGroupScaledEnabled = currentStrategy.graphScaledToMax;
            minProfitability = currentStrategy.minProfitability
                ? currentStrategy.minProfitability
                : currentStrategy.medProfitability;
        } else {
            const minProfitObj = strategies.reduce((prev, curr) => {
                return (prev.minProfitability || prev.medProfitability) <
                    (curr.minProfitability || curr.medProfitability)
                    ? prev
                    : curr;
            }, 1);

            if (minProfitObj.recurringContributionAmount) {
                isRecurringWidthScaled =
                    bigBarScale > minProfitObj.recurringContributionAmount;
            }

            minProfitability = minProfitObj.minProfitability
                ? minProfitObj.minProfitability
                : minProfitObj.medProfitability;
        }
        // isBarWidthScaled is used to display scaled message
        isBarWidthScaled =
            smallBarScale > minProfitability ||
            isInitialAmountWidthScaled ||
            isRecurringWidthScaled;

        return {
            graphScaledToMaxValue: graphScaledToMaxValuePoniter,
            isBarWidthScaled: isBarWidthScaled,
            isGroupScaledEnabled: isGroupScaledEnabled,
            isLeftBarExceedGoal: isLeftBarExceedGoal,
        };
    }

    buildStrategy(strategy, goalBar, graphScaleToMax) {
        const {
            id,
            maxProfitability,
            medProfitability,
            minProfitability,
            recurringContributionAmount,
        } = strategy;
        const { goal, showDescription, initialContributionAmount } = this.props;
        // scaled view section if bar is less than 30% of goal
        const scaledBarClassName = classnames({
            'ra-strategies-graph__strategy-bars-scaled':
                graphScaleToMax.isGroupScaledEnabled,
        });

        return (
            <Fragment>
                {showDescription ? (
                    <div className="ra-strategies-graph__strategy-description">
                        <RAStrategy.Description id={id} />
                    </div>
                ) : null}
                <div className="ra-strategies-graph__strategy-bars-container">
                    <div className="ra-strategies-graph__strategy-bars">
                        <RAStrategy
                            goal={goal}
                            goalBar={goalBar}
                            initialContributionAmount={
                                initialContributionAmount
                            }
                            maxProfitability={maxProfitability}
                            minProfitability={minProfitability}
                            medProfitability={medProfitability}
                            graphScaledToMaxEnabled={
                                graphScaleToMax.isGroupScaledEnabled
                            }
                            graphScaledToMaxValue={
                                graphScaleToMax.graphScaledToMaxValue
                            }
                            isLeftBarExceedGoal={
                                graphScaleToMax.isLeftBarExceedGoal
                            }
                            recurringContributionAmount={
                                recurringContributionAmount
                            }
                        />
                    </div>
                    <div className={scaledBarClassName}></div>
                </div>
            </Fragment>
        );
    }

    buildGraphSummaryLegalTextSection(currentSelectedStrategy) {
        const {
            objectiveEndDate,
            initialContributionAmount,
            goal,
            userName,
        } = this.props;

        const {
            maxProfitability,
            minProfitability,
            recurringContributionAmount,
            id,
        } = currentSelectedStrategy;

        const goalEndDate = formatText(
            'raStrategiesGraph-raStrategiesGraph_endDateCustom',
            [
                getDay(objectiveEndDate),
                toFirstUppercase(getMonthNameFromDate(objectiveEndDate)),
                getFullYear(objectiveEndDate),
            ],
        );

        const minimumExpectedValue =
            initialContributionAmount +
            recurringContributionAmount -
            minProfitability;
        const maximumExpectedValue =
            initialContributionAmount +
            recurringContributionAmount +
            maxProfitability;

        const initialContributionAmountFormatted = CurrencyHelper.getFormattedString(
            { amount: initialContributionAmount, currency: EURO_SYMBOL },
        );
        const recurringContributionAmountFormatted = CurrencyHelper.getFormattedString(
            { amount: recurringContributionAmount, currency: EURO_SYMBOL },
        );
        const minProfitabilityFormatted = CurrencyHelper.getFormattedString({
            amount: minProfitability,
            currency: EURO_SYMBOL,
        });
        const maxProfitabilityFormatted = CurrencyHelper.getFormattedString({
            amount: maxProfitability,
            currency: EURO_SYMBOL,
        });
        const minimumExpectedValueFormatted = CurrencyHelper.getFormattedString(
            { amount: minimumExpectedValue, currency: EURO_SYMBOL },
        );
        const maximumExpectedValueFormatted = CurrencyHelper.getFormattedString(
            { amount: maximumExpectedValue, currency: EURO_SYMBOL },
        );
        const strategyName = formatText(`raStrategyItem-raStrategyItem_${id}`);

        let injectedStrings = [
            strategyName,
            initialContributionAmountFormatted,
            recurringContributionAmountFormatted,
            minProfitabilityFormatted,
            maxProfitabilityFormatted,
            minimumExpectedValueFormatted,
            maximumExpectedValueFormatted,
            goalEndDate,
        ];

        let legalText2 = 'legalText2';
        if (recurringContributionAmount < MINIMUM_CONTRIBUTION_AMOUNT) {
            legalText2 = 'legalText2NoPeriodicContribution';
            injectedStrings = [
                strategyName,
                initialContributionAmountFormatted,
                minProfitabilityFormatted,
                maxProfitabilityFormatted,
                minimumExpectedValueFormatted,
                maximumExpectedValueFormatted,
                goalEndDate,
            ];
        } else if (maximumExpectedValue < goal) {
            legalText2 = 'legalText2NotReachingGoal';
        }

        const legalText2Value = `raStrategiesGraph-raStrategiesGraph_${legalText2}`;
        return (
            <ContactCenterRender>
                <div className="ra-strategies-graph__block-info-container">
                    <BlockInfo extraClass="ra-strategies-graph__block-info">
                        <FormattedText
                            className="ra-strategies-graph__block-info-title"
                            value="raStrategiesGraph-raStrategiesGraph_legalTextTitle"
                        />
                        <FormattedText
                            className="ra-strategies-graph__block-info-text"
                            value="raStrategiesGraph-raStrategiesGraph_legalText1"
                            injectedStrings={[userName]}
                        />
                        <FormattedText
                            className="ra-strategies-graph__block-info-text"
                            value={legalText2Value}
                            injectedStrings={injectedStrings}
                        />
                        <FormattedText
                            className="ra-strategies-graph__block-info-text"
                            value="raStrategiesGraph-raStrategiesGraph_legalText3"
                            injectedStrings={[userName]}
                        />
                    </BlockInfo>
                </div>
            </ContactCenterRender>
        );
    }

    buildStrategiesSection(singleStrategy) {
        const {
            selectedStrategy,
            onStrategyClick,
            immExpectedProfitability,
            maxInvestmentProfile,
            maxAvailableInvestmentProfile,
            initialContributionAmount,
            goal,
            showLegalText,
        } = this.props;

        const strategies = getFilteredStrategies(
            initialContributionAmount,
            immExpectedProfitability,
            maxInvestmentProfile,
            goal,
        );
        let content;
        let graphScaleToMax = this.getGraphScaledToMaxValues(strategies);
        let informativeMessageType = null;

        if (
            graphScaleToMax.isGroupScaledEnabled ||
            graphScaleToMax.isBarWidthScaled
        ) {
            informativeMessageType = BAR_GRAPH_SCALED;
        } else if (graphScaleToMax.isLeftBarExceedGoal) {
            informativeMessageType = INVESTMENT_EXCEED_GOAL;
        }

        const informativeMessageContent = informativeMessageType
            ? this.buildInformativeMessageContent(informativeMessageType)
            : null;

        if (singleStrategy && strategies.length > 0) {
            const currentSelectedStrategy = strategies.find(function(s) {
                return s.id === selectedStrategy;
            });
            const goalBar = this.buildGoalBar(singleStrategy);
            const graphSummaryLegalTextSection =
                showLegalText &&
                this.buildGraphSummaryLegalTextSection(currentSelectedStrategy);

            content = (
                <div>
                    <div
                        key={currentSelectedStrategy.id}
                        className="ra-strategies-graph__strategy-card ra-strategies-graph__strategy-card--single"
                    >
                        {this.buildStrategy(
                            currentSelectedStrategy,
                            goalBar,
                            graphScaleToMax,
                        )}
                    </div>
                    {informativeMessageContent}
                    {graphSummaryLegalTextSection}
                </div>
            );
        } else {
            content = strategies.map((strategy, index) => {
                const id = strategy.id;
                const isClickeable =
                    !!onStrategyClick && maxAvailableInvestmentProfile > index;
                const strategyClass = classnames(
                    'ra-strategies-graph__strategy-card',
                    {
                        'ra-strategies-graph__strategy-card--selected':
                            id === selectedStrategy &&
                            maxAvailableInvestmentProfile > index,
                        'ra-strategies-graph__strategy-card--available':
                            isClickeable &&
                            selectedStrategy !== id &&
                            maxAvailableInvestmentProfile > index,
                        'ra-strategies-graph__strategy-card--disabled': !isClickeable,
                    },
                );
                const goalBar = index === 0 ? this.buildGoalBar() : null;
                const handleClick = () => isClickeable && onStrategyClick(id);

                return (
                    <div
                        key={id}
                        className={strategyClass}
                        onClick={handleClick}
                    >
                        {this.buildStrategy(strategy, goalBar, graphScaleToMax)}
                    </div>
                );
            });
            if (informativeMessageContent) {
                content.push(informativeMessageContent);
            }
        }

        return content;
    }

    buildGoalBar(singleStrategy) {
        const { goal, objectiveEndDate } = this.props;
        const goalHeight = singleStrategy ? '183px' : this.state.goalHeight;

        const goalAmount = CurrencyHelper.getFormattedString({
            amount: goal,
            currency: EURO_SYMBOL,
        });
        const goalText = formatText('raStrategiesGraph-goalText');
        let endDate = '';
        if (objectiveEndDate) {
            endDate = [
                getDay(objectiveEndDate),
                toFirstUppercase(getMonthNameFromDate(objectiveEndDate)),
                getFullYear(objectiveEndDate),
            ].join(' ');
        }
        return (
            <RAGoalBar
                height={goalHeight}
                amount={goalAmount}
                text={goalText}
                date={endDate}
            />
        );
    }

    buildMinProfitabilityHeaderContent(min, max) {
        const wrapperClassName = classnames(
            'ra-strategies-graph__header-rentability-bar',
            {
                'ra-strategies-graph__header-rentability-bar--min--red': min,
                'ra-strategies-graph__header-rentability-bar--min--lightgreen': max,
                'ra-strategies-graph__header-rentability-bar--min--red-lightgreen':
                    min && max,
            },
        );
        return (
            <div className="ra-strategies-graph__header-rentability-expected-titles">
                <div className={wrapperClassName} />
                <div className="ra-strategies-graph__header-rentability-expected-title">
                    <FormattedText value="raStrategiesGraph-minExpected" />
                </div>
            </div>
        );
    }

    buildMinProfitabilityDescriptionContent() {
        return (
            <div className="ra-strategies-graph__header-rentability-description">
                <div className="ra-strategies-graph__header-rentability-description-text">
                    <FormattedText value="raStrategiesGraph-negative" />
                </div>
                <div className="ra-strategies-graph__header-rentability-description-draw">
                    <div className="ra-strategies-graph__header-rentability-description-bar ra-strategies-graph__header-rentability-description-bar--neg" />
                    <div className="ra-strategies-graph__header-rentability-description-lines" />
                    <div className="ra-strategies-graph__header-rentability-description-bar ra-strategies-graph__header-rentability-description-bar--pos" />
                </div>
                <div className="ra-strategies-graph__header-rentability-description-text">
                    <FormattedText value="raStrategiesGraph-positive" />
                </div>
            </div>
        );
    }

    buildFooter(singleStrategy) {
        const {
            goal,
            objectiveEndDate,
            selectedStrategy,
            maxInvestmentProfile,
            maxAvailableInvestmentProfile,
            immExpectedProfitability,
            initialContributionAmount,
            sourceUrl,
            prePurchaseConfirmationMessage,
        } = this.props;
        const strategies = getFilteredStrategies(
            initialContributionAmount,
            immExpectedProfitability,
            maxInvestmentProfile,
            goal,
        );
        if (strategies.length === 0) {
            return null;
        }

        // currentSelectedStrategy can also be undefined if selectedStrategy has a value greater than maxInvestmentProfile
        const currentSelectedStrategy = strategies.find(
            s =>
                s.id === selectedStrategy &&
                selectedStrategy <= maxAvailableInvestmentProfile,
        );

        let leftContent = null;
        let rightContent = null;
        if (currentSelectedStrategy && goal > 0 && objectiveEndDate) {
            const medProfitability = currentSelectedStrategy.medProfitability;
            const minProfitabilitySign = medProfitability
                ? PLUS_SIGN
                : MINUS_SIGN;
            const minProfitabilityValue =
                medProfitability || currentSelectedStrategy.minProfitability;
            const minProfitability = (
                <span className="ra-strategies-graph__footer-info-strong">
                    {minProfitabilitySign +
                        CurrencyHelper.getFormattedString({
                            amount: minProfitabilityValue,
                            currency: EURO_SYMBOL,
                        })}
                </span>
            );
            const maxProfitability = (
                <span className="ra-strategies-graph__footer-info-strong">
                    {CurrencyHelper.getFormattedString({
                        amount: currentSelectedStrategy.maxProfitability,
                        currency: EURO_SYMBOL,
                    })}
                </span>
            );
            const goalAmount = (
                <span className="ra-strategies-graph__footer-info-strong">
                    {CurrencyHelper.getFormattedString({
                        amount: goal,
                        currency: EURO_SYMBOL,
                    })}
                </span>
            );
            const objectiveEndDateEl = (
                <span className="ra-strategies-graph__footer-info-strong">
                    <FormattedText
                        value="raStrategiesGraph-raStrategiesGraph_endDateCustom"
                        injectedStrings={[
                            getDay(objectiveEndDate),
                            toFirstUppercase(
                                getMonthNameFromDate(objectiveEndDate),
                            ),
                            getFullYear(objectiveEndDate),
                        ]}
                    />
                </span>
            );
            const leftContentValue =
                currentSelectedStrategy.up >= goal
                    ? 'raStrategiesGraph-footerStrategyInfo'
                    : 'raStrategiesGraph-raStrategiesGraph_footerStrategyInfoNotReach';
            leftContent = (
                <div className="ra-strategies-graph__footer-strategy-description">
                    <FormattedTextWithComponents
                        value={leftContentValue}
                        injectedComponents={[
                            goalAmount,
                            objectiveEndDateEl,
                            minProfitability,
                            maxProfitability,
                        ]}
                    />
                </div>
            );

            const { id } = currentSelectedStrategy;
            const url =
                !!sourceUrl &&
                `/myprofile/robo/${sourceUrl}/portfolioDistribution/${id}/${initialContributionAmount}/2`;

            if (!singleStrategy) {
                rightContent = (
                    <OKLink
                        modifier="complementary"
                        to={url}
                        onClick={this.handlePortfolioDistributionClick}
                    >
                        <FormattedText value="raStrategiesGraph-showPortfolioDistribution" />
                    </OKLink>
                );
            }
        }
        const footerClassName = classnames('ra-strategies-graph__footer', {
            'ra-strategies-graph__footer--single': singleStrategy,
        });

        if (__CONTACT_CENTER__) {
            if (prePurchaseConfirmationMessage) {
                return (
                    <footer className={footerClassName}>
                        <div className="ra-strategies-graph__footer-row-cc">
                            <BlockInfo>
                                {prePurchaseConfirmationMessage}
                                <div className="ra-strategies-graph__footer-right-cc">
                                    {rightContent}
                                </div>
                            </BlockInfo>
                        </div>
                    </footer>
                );
            }
        } else {
            return (
                <footer className={footerClassName}>
                    <div className="ra-strategies-graph__footer-row">
                        <div className="ra-strategies-graph__footer-left">
                            {leftContent}
                        </div>
                        <div className="ra-strategies-graph__footer-right">
                            {rightContent}
                        </div>
                    </div>
                </footer>
            );
        }
    }

    buildInformativeMessageContent(infoType) {
        const infoTypeMessageKey =
            infoType === BAR_GRAPH_SCALED
                ? 'graphScaledMessage'
                : 'investmentExceedThanGoalMessage';
        const infoTypeMessageValue = `raStrategiesGraph-raStrategiesGraph_${infoTypeMessageKey}`;
        return (
            <div
                className="ra-strategies-graph__strategy-information-section"
                key={infoTypeMessageKey}
            >
                <OKMessage type="informative">
                    <FormattedText value={infoTypeMessageValue} />
                </OKMessage>
            </div>
        );
    }

    buildRentabilityHeader(strategiesContent) {
        const graphScaleToMax = this.getGraphScaledToMaxValues(
            strategiesContent,
        );
        if (graphScaleToMax.isLeftBarExceedGoal) {
            return null;
        }
        const { selectedStrategy, singleStrategy } = this.props;
        let showMinProfitability = false,
            showMedProfitability = false;
        strategiesContent.map(strategy => {
            if (!!strategy.minProfitability) {
                showMinProfitability = true;
            } else if (!!strategy.maxProfitability) {
                showMedProfitability = true;
            }
        });

        if (singleStrategy && strategiesContent.length > 0) {
            const currentStrategy = strategiesContent.find(function(s) {
                return s.id === selectedStrategy;
            });
            showMinProfitability = !!currentStrategy.minProfitability;
            showMedProfitability = !!currentStrategy.medProfitability;
        }
        const minProfitabilityHeaderContent =
            showMinProfitability || showMedProfitability
                ? this.buildMinProfitabilityHeaderContent(
                      showMinProfitability,
                      showMedProfitability,
                  )
                : '';
        const minProfitabilityDescriptionContent =
            showMinProfitability && showMedProfitability
                ? this.buildMinProfitabilityDescriptionContent()
                : '';

        return (
            <div className="ra-strategies-graph__header-rentability">
                <div className="ra-strategies-graph__header-rentability-title">
                    <FormattedText value="raStrategiesGraph-rentability" />
                </div>
                <div className="ra-strategies-graph__header-rentability-expected">
                    <div className="ra-strategies-graph__header-rentability-expected-titles">
                        <div className="ra-strategies-graph__header-rentability-bar ra-strategies-graph__header-rentability-bar--max" />
                        <div className="ra-strategies-graph__header-rentability-expected-title">
                            <FormattedText value="raStrategiesGraph-maxExpected" />
                        </div>
                    </div>
                    {minProfitabilityHeaderContent}
                </div>
                {minProfitabilityDescriptionContent}
            </div>
        );
    }

    render() {
        const {
            immExpectedProfitability,
            currentValuationKey,
            showDescription,
            initialContributionAmount,
            singleStrategy,
            maxInvestmentProfile,
            goal,
        } = this.props;
        const contributionClass = classnames(
            'ra-strategies-graph__header-contributions',
            {
                'ra-strategies-graph__header-contributions--padding-left': showDescription,
            },
        );

        const initialContributionAmountTitle = !!currentValuationKey
            ? formatText(currentValuationKey)
            : formatText('raStrategiesGraph-initialContributionAmountTitle');
        const recurringContributionAmountTitle = formatText(
            'raStrategiesGraph-recurringContributionAmountTitle',
        );

        let recurringContributionAmount = 0;
        const immProductsChartData = immExpectedProfitability.get(
            'barChartData',
        );
        if (immProductsChartData) {
            const contribution = immProductsChartData.getIn([
                0,
                1,
                'contributionNet',
            ]);
            recurringContributionAmount =
                contribution - initialContributionAmount;
        }

        const footer = this.buildFooter(singleStrategy);
        const wrapperClassName = classnames('ra-strategies-graph', {
            'ra-strategies-graph--single': singleStrategy,
        });

        const strategiesContent = getFilteredStrategies(
            initialContributionAmount,
            immExpectedProfitability,
            maxInvestmentProfile,
            goal,
        );
        const strategies = this.buildStrategiesSection(singleStrategy);
        const rentabilityHeader = this.buildRentabilityHeader(
            strategiesContent,
        );

        return (
            <section className={wrapperClassName}>
                <header className="ra-strategies-graph__header">
                    <div className={contributionClass}>
                        <RAContribution
                            color="#002B45"
                            title={initialContributionAmountTitle}
                            amount={initialContributionAmount}
                        />
                        <RAContribution
                            color="#546E7A"
                            title={recurringContributionAmountTitle}
                            amount={recurringContributionAmount}
                        />
                    </div>
                    <WebappRender>{rentabilityHeader}</WebappRender>
                    <ContactCenterRender>
                        <div className="ra-strategies-graph__header-rentability">
                            <div className="ra-strategies-graph__header-rentability-title">
                                <FormattedText value="raStrategiesGraph-rentability" />
                            </div>
                            <div className="ra-strategies-graph__header-rentability-expected">
                                <div className="ra-strategies-graph__header-rentability-expected-titles">
                                    <div className="ra-strategies-graph__header-rentability-bar ra-strategies-graph__header-rentability-bar--max" />
                                    <div className="ra-strategies-graph__header-rentability-expected-title">
                                        <FormattedText value="raStrategiesGraph-maxExpected" />
                                    </div>
                                </div>
                                <div className="ra-strategies-graph__header-rentability-expected-titles">
                                    <div className="ra-strategies-graph__header-rentability-bar ra-strategies-graph__header-rentability-bar--min" />
                                    <div className="ra-strategies-graph__header-rentability-expected-title">
                                        <FormattedText value="raStrategiesGraph-minExpected" />
                                    </div>
                                </div>
                            </div>
                            <div className="ra-strategies-graph__header-rentability-description">
                                <div className="ra-strategies-graph__header-rentability-description-text">
                                    <FormattedText value="raStrategiesGraph-negative" />
                                </div>
                                <div className="ra-strategies-graph__header-rentability-description-draw">
                                    <div className="ra-strategies-graph__header-rentability-description-bar ra-strategies-graph__header-rentability-description-bar--neg" />
                                    <div className="ra-strategies-graph__header-rentability-description-lines" />
                                    <div className="ra-strategies-graph__header-rentability-description-bar ra-strategies-graph__header-rentability-description-bar--pos" />
                                </div>
                                <div className="ra-strategies-graph__header-rentability-description-text">
                                    <FormattedText value="raStrategiesGraph-positive" />
                                </div>
                            </div>
                        </div>
                    </ContactCenterRender>
                </header>
                <div className="ra-strategies-graph__strategies">
                    {strategies}
                </div>
                {footer}
            </section>
        );
    }
}

RAStrategiesGraph.defaultProps = {
    showDescription: true,
    singleStrategy: false,
    showLegalText: false,
    sourceUrl: null,
};

RAStrategiesGraph.propTypes = {
    currentValuationKey: PropTypes.string,
    goal: PropTypes.number.isRequired,
    immExpectedProfitability: PropTypes.object.isRequired,
    initialContributionAmount: PropTypes.number.isRequired,
    maxAvailableInvestmentProfile: PropTypes.number.isRequired,
    maxInvestmentProfile: PropTypes.number.isRequired,
    objectiveEndDate: PropTypes.object.isRequired,
    onStrategyClick: PropTypes.func,
    prePurchaseConfirmationMessage: PropTypes.object,
    showDescription: PropTypes.bool.isRequired,
    showLegalText: PropTypes.bool.isRequired,
    selectedStrategy: PropTypes.number.isRequired,
    singleStrategy: PropTypes.bool,
    sourceUrl: PropTypes.string,
    userName: PropTypes.string,
};

module.exports = RAStrategiesGraph;
